import { log } from "utils/logger";

// ──────────────────────────────────────────────────────────────────────────────────────────────
// TEMP diagnostic. Network sibling of debugTrace.ts: mirrors network events into an in-memory ring
// buffer so the on-screen NetworkDebugOverlay can show — and let you copy — them directly on a kiosk
// tablet (Hexnode Browser Lite), where remote Web Inspector is unavailable. Captures online/offline +
// network-change events, every HTTP request, and the WebSocket subscription lifecycle (broken /
// reconnected). Lines are tagged [http] / [gql] / [ws] / [conn] so the single feed stays scannable.
//
// Enabled on ANY tablet (network problems hit standard tablets too, not just the master) when the
// `networkDebug` flag is set (URL `?networkDebug=true` is persisted to localStorage so it survives
// kiosk reloads; `?networkDebug=false` clears it). It stays off for customers by default.
//
// Remove this file together with installNetworkDebug.ts and NetworkDebugOverlay.tsx, plus the
// shouldTraceNetworkDebug()-gated calls in ApolloClientProvider / DeviceContext /
// useReconnectAfterBrokenSubscription / main.tsx / App.tsx, once network issues are diagnosed.
// ──────────────────────────────────────────────────────────────────────────────────────────────

const MAX_LINES = 500;
let lines: string[] = [];
const listeners = new Set<() => void>();

const notify = () => {
	for (const listener of listeners) {
		listener();
	}
};

const pad = (value: number) => String(value).padStart(2, "0");
const timestamp = () => {
	const now = new Date();
	return `${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`;
};

// Only the low-frequency [conn]/[ws] lines are mirrored to the console; the high-volume [http]/[gql]
// lines stay in the on-screen buffer only, so a long WKWebView capture can't grow console retention.
const shouldMirrorToConsole = (message: string) => message.startsWith("[conn]") || message.startsWith("[ws]");

/** Append to the on-screen overlay buffer (and mirror [conn]/[ws] lines to the console). */
export const networkTrace = (message: string) => {
	if (shouldMirrorToConsole(message)) {
		log(message, "info");
	}
	const line = `${timestamp()} ${message}`;
	lines = lines.length >= MAX_LINES ? [...lines.slice(1), line] : [...lines, line];
	notify();
};

export const subscribeNetworkTrace = (listener: () => void) => {
	listeners.add(listener);
	return () => {
		listeners.delete(listener);
	};
};

// Stable reference between writes → safe for useSyncExternalStore.
export const getNetworkTraceSnapshot = () => lines;

export const clearNetworkTrace = () => {
	lines = [];
	notify();
};

export const isNetworkDebugFlagEnabled = (): boolean => {
	try {
		const param = new URLSearchParams(window.location.search).get("networkDebug");
		if (param === "true") {
			localStorage.setItem("networkDebug", "true");
		} else if (param === "false") {
			localStorage.removeItem("networkDebug");
		}
		return localStorage.getItem("networkDebug") === "true";
	} catch {
		return false;
	}
};

/** True whenever the diagnostic flag is enabled — on any tablet (unlike the master-only lighting trace). */
export const shouldTraceNetworkDebug = (): boolean => isNetworkDebugFlagEnabled();

// TEMP audio diagnostic: snapshot of the Howler global so the kiosk overlay can show whether the
// AudioContext is suspended (the touch-less WKWebView gesture problem that drops .play()). Shared by
// AlarmAudioProvider (load trace) and useOpenDoorAlarm (play traces). Reads `window.Howler` defensively
// — it carries no type. Remove with the rest of the [audio] network-debug scaffolding.
export const howlerState = (): string => {
	const howler = (
		window as unknown as {
			Howler?: { usingWebAudio?: boolean; ctx?: { state?: string }; _muted?: boolean; volume?: () => number };
		}
	).Howler;
	if (!howler) {
		return "Howler=none";
	}
	return `usingWebAudio=${howler.usingWebAudio} ctx=${howler.ctx?.state ?? "none"} muted=${howler._muted} vol=${howler.volume?.()}`;
};
